import { useEffect, useState, useCallback } from "react";
import ModalShell from "@/components/ModalShell";
import { X, Loader2 } from "lucide-react";

/**
 * In-app replacement for window.prompt(). Consumers open it imperatively via
 * the exported `promptDialog({title, placeholder, defaultValue}) → Promise<string|null>`.
 *
 * Renders a single instance mounted at App root; call the promise-based API.
 * A small event-bus keeps it dependency-free.
 */
const bus = new EventTarget();
let resolver = null;

export function promptDialog(opts = {}) {
  return new Promise((resolve) => {
    resolver = resolve;
    bus.dispatchEvent(new CustomEvent("open", { detail: opts }));
  });
}

export default function PromptDialog() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onOpen(e) {
      setOpts(e.detail || {});
      setValue(e.detail?.defaultValue || "");
      setBusy(false);
      setOpen(true);
    }
    bus.addEventListener("open", onOpen);
    return () => bus.removeEventListener("open", onOpen);
  }, []);

  const close = useCallback((result) => {
    setOpen(false);
    if (resolver) {
      const r = resolver;
      resolver = null;
      r(result);
    }
  }, []);

  function submit(e) {
    e?.preventDefault?.();
    const v = value.trim();
    if (opts.required !== false && !v) return;
    setBusy(true);
    close(v);
  }

  return (
    <ModalShell open={open} onClose={() => close(null)} maxWidth="max-w-md" testId="prompt-dialog">
      <button
        type="button"
        onClick={() => close(null)}
        className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm"
        data-testid="prompt-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
        {opts.eyebrow || "Input"}
      </div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight">
        {opts.title || "Enter a value"}
      </h2>
      {opts.description && (
        <p className="text-sm text-muted-foreground mt-2">{opts.description}</p>
      )}

      <form onSubmit={submit} className="mt-5 space-y-4">
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={opts.placeholder || ""}
          className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground"
          data-testid="prompt-input"
        />
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => close(null)}
            className="border border-border px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-surface-elev"
            data-testid="prompt-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 disabled:opacity-50"
            data-testid="prompt-submit"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {opts.submitLabel || "OK"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
